// @ts-nocheck -- console: vendored as delivered; its callers still get its prop types.
// Runqo Console components, vendored from the Claude Design system. Local edits are marked `console:`.
import { ICONS } from './icons';
import { LOGO_PATH, LOGO_VIEWBOX } from './logo';
import { STATUS, WARNINGS, REASONS, HEALTH, MATCH, TRUST, TRIGGERS, CATEGORIES, ORGANIZATION_TYPES, MEMBER_ROLES, SURVEY_TYPES, ANSWER_TYPES, LINK_LINE, DECISIONS, RULE_ACTIVITY, RULE_FIGURES, RULES_PENDING, TRIGGER_RETRY, TRIGGER_RETRY_OF, LINK_ORIGINS, GROUP_ACTIONS, GROUP_RESOLUTIONS, GROUP_SKIPS, PIN, UNLINK, KEYS_HOLD, LINK_SEARCH, DECIDE_WHY_ID, type Tone } from './vocab'; // console: type-only import, organization, member, survey, link-line, decision, rule-row, rules-pending, retry-trigger and group-rule, pin, unlink, keys-hold and link-search types

// console: React is imported instead of read from window.React.
import * as React from 'react';
const { useState, useRef, useEffect, useMemo, useCallback, Fragment } = React as any;

// console: anchors render through the host's link component, so internal hrefs route client-side.
export const DsLinkContext = React.createContext<React.ElementType>('a');
function A(props: any) {
  const L = React.useContext(DsLinkContext);
  return <L {...props} />;
}
type Node = any;

const cx = (...a: any[]) => a.filter(Boolean).join(' ');
let idSeq = 0;
function useUid(prefix = 'rq') {
  const r = useRef(null);
  if (r.current === null) r.current = prefix + '-' + (++idSeq);
  return r.current as string;
}

export const vocab = { STATUS, WARNINGS, REASONS, HEALTH, MATCH, TRUST, TRIGGERS, CATEGORIES, ORGANIZATION_TYPES, MEMBER_ROLES, SURVEY_TYPES, ANSWER_TYPES, LINK_LINE, DECISIONS, RULE_ACTIVITY, RULE_FIGURES, RULES_PENDING, TRIGGER_RETRY, TRIGGER_RETRY_OF, LINK_ORIGINS, GROUP_ACTIONS, GROUP_RESOLUTIONS, GROUP_SKIPS, PIN, UNLINK, KEYS_HOLD, LINK_SEARCH }; // console: organization, member, survey, link-line, decision, rule-row, rules-pending, retry-trigger and group-rule, pin, unlink, keys-hold and link-search types

/* ───────────── Brand ───────────── */

/** An icon from the Lucide subset. Decorative unless `label` is given. */
export function Icon({ name, size = 16, label, className, strokeWidth, ...rest }: { name: string; size?: number; label?: string; className?: string; strokeWidth?: number; [k: string]: any }) {
  const nodes = ICONS[name] || [];
  return (
    <svg className={cx('rq-icon', className)} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth ?? (size <= 14 ? 2.25 : 2)} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden={label ? undefined : 'true'} role={label ? 'img' : undefined} aria-label={label} focusable="false" {...rest}>
      {nodes.map(([t, a], i) => React.createElement(t, { key: i, ...a }))}
    </svg>
  );
}

/** The Runqo mark (the runner's stride), alone or as the Runqo Console lockup. */
export function Logo({ variant = 'lockup', size = 24, product = 'Console', className }: { variant?: 'mark' | 'lockup'; size?: number; product?: string | null; className?: string }) {
  const mark = (
    <svg className="rq-logo-mark" viewBox={LOGO_VIEWBOX} height={size} width={size * 1.4117} aria-hidden={variant === 'lockup' ? 'true' : undefined}
      role={variant === 'mark' ? 'img' : undefined} aria-label={variant === 'mark' ? 'Runqo' : undefined} focusable="false">
      <path d={LOGO_PATH} fill="currentColor" />
    </svg>
  );
  if (variant === 'mark') return <span className={cx('rq-logo', className)}>{mark}</span>;
  return (
    <span className={cx('rq-logo', className)} style={{ fontSize: Math.round(size * 0.75) + 'px' }}>
      {mark}
      <span className="rq-logo-word">Runqo</span>
      {product ? <span className="rq-logo-product">{product}</span> : null}
    </span>
  );
}

/* ───────────── Actions ───────────── */

export interface ButtonProps { variant?: 'primary' | 'secondary' | 'quiet' | 'danger' | 'danger-quiet'; size?: 'sm' | 'md' | 'lg'; icon?: string; iconRight?: string; loading?: boolean; shortcut?: string; iconOnly?: boolean; label?: string; href?: string; children?: Node; className?: string; [k: string]: any }
/** Buttons are verbs. One primary per view. */
export function Button({ variant = 'secondary', size = 'md', icon, iconRight, loading, shortcut, iconOnly, label, href, children, className, disabled, ...rest }: ButtonProps) {
  const cls = cx('rq-btn', 'rq-btn--' + variant, 'rq-btn--' + size, iconOnly && 'rq-btn--icon', loading && 'is-loading', className);
  const text = iconOnly ? <span className="rq-sr">{label || children}</span> : children;
  const inner = (
    <Fragment>
      {loading ? <Icon name="loader-circle" className="rq-spin" /> : icon ? <Icon name={icon} /> : null}
      {text != null ? <span className="rq-btn-label">{text}</span> : null}
      {iconRight && !loading ? <Icon name={iconRight} /> : null}
      {shortcut && !iconOnly ? <Kbd subtle>{shortcut}</Kbd> : null}
    </Fragment>
  );
  const title = iconOnly ? (label || (typeof children === 'string' ? children : undefined)) + (shortcut ? ' (' + shortcut + ')' : '') : undefined;
  if (href && !disabled) return <A className={cls} href={href} title={title} {...rest}>{inner}</A>;
  return <button type="button" className={cls} disabled={disabled} aria-busy={loading ? 'true' : undefined} title={title} {...rest}>{inner}</button>;
}

/** A keyboard key, for shortcut hints. */
export function Kbd({ children, subtle }: { children: Node; subtle?: boolean }) {
  return <kbd className={cx('rq-kbd', subtle && 'rq-kbd--subtle')}>{children}</kbd>;
}

/* ───────────── Forms ───────────── */

interface FieldShell { label: string; hint?: Node; error?: Node; required?: boolean; optional?: boolean; hideLabel?: boolean; id?: string }
function Shell({ id, label, hint, error, required, optional, hideLabel, children }: FieldShell & { children: Node }) {
  return (
    <div className={cx('rq-field', error && 'is-invalid')}>
      <label htmlFor={id} className={cx('rq-field-label', hideLabel && 'rq-sr')}>{label}{required ? <span className="rq-field-req"> (required)</span> : optional ? <span className="rq-field-opt"> (optional)</span> : null}</label>
      {children}
      {error ? <p className="rq-field-error" id={id + '-err'}><Icon name="octagon-alert" size={14} />{error}</p> : hint ? <p className="rq-field-hint" id={id + '-hint'}>{hint}</p> : null}
    </div>
  );
}

/** A labelled text input with hint and inline error. */
export function TextField({ label, hint, error, required, optional, hideLabel, icon, mono, size = 'md', id, className, ...rest }: FieldShell & { icon?: string; mono?: boolean; size?: 'sm' | 'md' | 'lg'; className?: string; [k: string]: any }) {
  const uid = useUid('rq-tf'); const fid = id || uid;
  return (
    <Shell id={fid} label={label} hint={hint} error={error} required={required} optional={optional} hideLabel={hideLabel}>
      <div className={cx('rq-input', 'rq-input--' + size, icon && 'has-icon', mono && 'is-mono', className)}>
        {icon ? <Icon name={icon} /> : null}
        <input id={fid} aria-invalid={error ? 'true' : undefined} aria-describedby={error ? fid + '-err' : hint ? fid + '-hint' : undefined} required={required} {...rest} />
      </div>
    </Shell>
  );
}

/** A labelled native select. */
export function Select({ label, hint, error, required, optional, hideLabel, options, size = 'md', id, ...rest }: FieldShell & { options: (string | { value: string; label: string })[]; size?: 'sm' | 'md' | 'lg'; [k: string]: any }) {
  const uid = useUid('rq-sel'); const fid = id || uid;
  return (
    <Shell id={fid} label={label} hint={hint} error={error} required={required} optional={optional} hideLabel={hideLabel}>
      <div className={cx('rq-input', 'rq-select', 'rq-input--' + size)}>
        <select id={fid} aria-invalid={error ? 'true' : undefined} aria-describedby={error ? fid + '-err' : hint ? fid + '-hint' : undefined} {...rest}>
          {options.map((o: any) => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Icon name="chevrons-up-down" />
      </div>
    </Shell>
  );
}

/** A checkbox with its label; supports the indeterminate state for "select all". */
export function Checkbox({ label, hideLabel, indeterminate, className, ...rest }: { label: Node; hideLabel?: boolean; indeterminate?: boolean; className?: string; [k: string]: any }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) (ref.current as any).indeterminate = !!indeterminate; }, [indeterminate]);
  return (
    <label className={cx('rq-check', className)}>
      <input ref={ref} type="checkbox" {...rest} />
      <span className="rq-check-box" aria-hidden="true"><Icon name={indeterminate ? 'minus' : 'check'} size={12} strokeWidth={3} /></span>
      <span className={cx('rq-check-label', hideLabel && 'rq-sr')}>{label}</span>
    </label>
  );
}

/* ───────────── Vocabulary ───────────── */

function Badge({ tone, icon, children, title, size = 'md', className }: { tone: Tone; icon?: string; children: Node; title?: string; size?: 'sm' | 'md'; className?: string }) {
  return (
    <span className={cx('rq-badge', 'rq-tone--' + tone, 'rq-badge--' + size, className)} title={title}>
      {icon ? <Icon name={icon} size={size === 'sm' ? 12 : 14} className={icon === 'loader-circle' ? 'rq-spin' : undefined} /> : null}
      <span>{children}</span>
    </span>
  );
}

/** A status word with its icon and tone. Never colour alone. */
export function Status({ domain = 'candidate', value, compact, size = 'md' }: { domain?: string; value: string; compact?: boolean; size?: 'sm' | 'md' }) {
  const t = (STATUS[domain] || {})[value];
  if (!t) return <Badge tone="neutral" icon="circle-help" size={size}>{value}</Badge>;
  return <Badge tone={t.tone} icon={t.icon} size={size} title={t.meaning}>{compact && t.short ? t.short : t.label}</Badge>;
}

/** A quality signal on a candidate. Missing city blocks publishing; the rest inform. */
export function Warning({ type, compact, detail, href, onClick }: { type: string; compact?: boolean; detail?: boolean; href?: string; onClick?: () => void }) {
  const w = WARNINGS[type];
  if (!w) return null;
  const label = compact && w.short ? w.short : w.label;
  const body = (
    <Fragment>
      <Icon name={w.blocking ? 'octagon-alert' : w.icon} size={14} className="rq-warn-icon" />
      <span className="rq-warn-label">{label}</span>
      {w.blocking && !compact ? <span className="rq-warn-block">Blocks publishing</span> : null}
    </Fragment>
  );
  const cls = cx('rq-warn', w.blocking && 'is-blocking', (href || onClick) && 'is-link');
  const chip = href ? <A className={cls} href={href} title={w.meaning}>{body}</A>
    : onClick ? <button type="button" className={cls} onClick={onClick} title={w.meaning}>{body}</button>
    : <span className={cls} title={w.meaning}>{body}</span>;
  if (!detail) return chip;
  return <div className="rq-warn-detail">{chip}<span className="rq-warn-meaning">{w.meaning}</span></div>;
}

/** Who did something: the pipeline (dashed, bot) or a person (solid, initials). */
export function Actor({ kind, name, mode = 'acted', rule, compact }: { kind: 'machine' | 'person'; name?: string; mode?: 'acted' | 'suggested'; rule?: string; compact?: boolean }) {
  if (kind === 'machine') {
    const label = mode === 'suggested' ? 'Suggested by pipeline' : 'Pipeline';
    return (
      <span className={cx('rq-actor', 'rq-actor--machine', mode === 'suggested' && 'is-suggested')} title={rule ? 'Rule: ' + rule : undefined}>
        <span className="rq-actor-avatar" aria-hidden="true"><Icon name="bot" size={12} /></span>
        <span className="rq-actor-name">{compact ? (mode === 'suggested' ? 'Suggested' : 'Pipeline') : label}</span>
        {rule && !compact ? <span className="rq-actor-rule">· {rule}</span> : null}
      </span>
    );
  }
  const initials = (name || '?').split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  return (
    <span className="rq-actor rq-actor--person">
      <span className="rq-actor-avatar" aria-hidden="true">{initials}</span>
      <span className="rq-actor-name">{name}</span>
    </span>
  );
}

/** The AI's confidence against the trust bar, as a number, a meter and a word. */
export function Confidence({ value, bar = 0.8, compact }: { value: number | null; bar?: number; compact?: boolean }) {
  if (value == null) return <span className="rq-conf is-missing"><span className="rq-missing">No score</span></span>;
  const pct = Math.round(value * 100); const below = value < bar;
  return (
    <span className={cx('rq-conf', below && 'is-below')} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
      aria-valuetext={pct + '%, ' + (below ? 'below' : 'above') + ' the trust bar of ' + Math.round(bar * 100) + '%'}>
      <span className="rq-conf-num">{pct}%</span>
      <span className="rq-conf-track" aria-hidden="true"><span className="rq-conf-fill" style={{ width: pct + '%' }} /><span className="rq-conf-bar" style={{ left: bar * 100 + '%' }} /></span>
      {compact ? (below ? <Icon name="triangle-alert" size={14} label="Below the trust bar" className="rq-conf-icon" /> : null)
        : <span className="rq-conf-word">{below ? <Fragment><Icon name="triangle-alert" size={14} className="rq-conf-icon" />Below bar</Fragment> : 'Above bar'}</span>}
    </span>
  );
}

/** How strongly two records look like the same race: three pips and a word. */
export function MatchStrength({ strength }: { strength: 'likely' | 'possible' | 'unlikely' }) {
  const m = MATCH[strength];
  return (
    <span className={cx('rq-match', 'rq-match--' + strength)} title={m.meaning}>
      <span className="rq-match-pips" aria-hidden="true">{[1, 2, 3].map((i) => <i key={i} className={i <= m.pips ? 'on' : ''} />)}</span>
      <span>{m.label}</span>
    </span>
  );
}

/** Source health in a word, optionally with its yield trend. */
export function Health({ status, trend, since }: { status: string; trend?: number[]; since?: string }) {
  const h = HEALTH[status] || HEALTH['not-run'];
  return (
    <span className="rq-health">
      <Badge tone={h.tone} icon={h.icon} title={h.meaning}>{h.label}</Badge>
      {since ? <span className="rq-health-since">{since}</span> : null}
      {trend ? <Sparkline values={trend} label={'Events found per run, last ' + trend.length + ' runs'} tone={h.tone === 'positive' ? 'neutral' : h.tone} /> : null}
    </span>
  );
}

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Formats a race date in Belgrade terms. A missing start time reads "to be announced", never midnight. */
export function formatEventDate(date: string | null, time?: string | null, opts: { compact?: boolean; weekday?: boolean; year?: boolean } = {}) {
  if (!date) return { date: 'No date', time: null, tba: false, missing: true };
  const [y, m, d] = date.split('-').map(Number);
  const wd = WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  const showYear = opts.year !== false;
  const ds = (opts.weekday !== false && !opts.compact ? wd + ' ' : '') + d + ' ' + MO[m - 1] + (showYear ? ' ' + y : '');
  if (time) return { date: ds, time, tba: false, missing: false };
  return { date: ds, time: opts.compact ? 'TBA' : 'Start time to be announced', tba: true, missing: false };
}
export function EventDate({ date, time, compact, weekday, year }: { date: string | null; time?: string | null; compact?: boolean; weekday?: boolean; year?: boolean }) {
  const f = formatEventDate(date, time, { compact, weekday, year });
  if (f.missing) return <span className="rq-date"><span className="rq-missing">No date</span></span>;
  return (
    <span className="rq-date">
      <time dateTime={date + (time ? 'T' + time : '')}>{f.date}</time>
      <span className="rq-date-sep" aria-hidden="true">·</span>
      <span className={cx('rq-date-time', f.tba && 'is-tba')} title={f.tba ? 'The organizer has not announced a start time yet.' : undefined}>{f.time}</span>
    </span>
  );
}

/** A race category as a neutral tag. Categories never take status colours. */
export function Category({ children }: { children: Node }) {
  return <span className="rq-cat">{children}</span>;
}

/* ───────────── Automation ───────────── */

/** Off / Suggest / Act for one action of one trust rule. */
export function TrustLevel({ value, onChange, label, name, disabledReason, showMeaning = true }: { value: 'off' | 'suggest' | 'act'; onChange?: (v: string) => void; label: string; name?: string; disabledReason?: string; showMeaning?: boolean }) {
  const uid = useUid('rq-trust'); const n = name || uid;
  return (
    <fieldset className={cx('rq-trust', 'is-' + value)} disabled={!!disabledReason}>
      <legend className="rq-trust-legend">{label}</legend>
      <div className="rq-seg" role="presentation">
        {(['off', 'suggest', 'act'] as const).map((k) => (
          <label key={k} className={cx('rq-seg-item', 'rq-seg-item--' + k, value === k && 'is-on')}>
            <input type="radio" name={n} value={k} checked={value === k} onChange={() => onChange && onChange(k)} />
            {k === 'suggest' ? <Icon name="bot" size={14} /> : k === 'act' ? <Icon name="play" size={14} /> : <Icon name="user" size={14} />}
            <span>{TRUST[k].label}</span>
          </label>
        ))}
      </div>
      {disabledReason ? <p className="rq-trust-meaning is-refused"><Icon name="ban" size={14} />{disabledReason}</p>
        : showMeaning ? <p className="rq-trust-meaning">{TRUST[value].meaning}</p> : null}
    </fieldset>
  );
}

/** Always says whether the machine is running, and how to stop it. */
export function KillSwitch({ scope = 'all', target, paused, detail, onToggle, compact, busy }: { scope?: 'all' | 'source'; target?: string; paused: boolean; detail?: string; onToggle?: () => void; compact?: boolean; busy?: boolean }) {
  // console: a source that isn't paused is "on", as automation is; "Running" means a run is collecting pages.
  const state = scope === 'all' ? (paused ? 'Automation paused' : 'Automation on') : (paused ? target + ' paused' : target + ' on');
  return (
    <div className={cx('rq-kill', paused ? 'is-paused' : 'is-on', compact && 'is-compact')} role="group" aria-label={scope === 'all' ? 'Automation' : 'Source ' + target}>
      <span className="rq-kill-state"><Icon name={paused ? 'pause' : 'play'} size={14} />{state}</span>
      {detail && !compact ? <span className="rq-kill-detail">{detail}</span> : null}
      <Button size="sm" variant={paused ? 'primary' : 'secondary'} icon={paused ? 'play' : 'pause'} onClick={onToggle} loading={busy}>{/* console: busy */}
        {paused ? 'Resume ' + (scope === 'all' ? 'automation' : 'source') : 'Pause ' + (scope === 'all' ? 'automation' : 'source')}
      </Button>
    </div>
  );
}

const money = (n: number, cur = '$') => cur + (n < 1 ? n.toFixed(3) : n.toFixed(2));
/** AI spend against the alert threshold and the hard ceiling. */
export function SpendMeter({ spent, alert, ceiling, period = 'last 30 days', currency = '$', compact, label }: { spent: number; alert: number; ceiling: number; period?: string; currency?: string; compact?: boolean; label?: string }) { // console: label names the provider for the meter
  const pct = Math.min(100, (spent / ceiling) * 100);
  const state = spent >= ceiling ? 'ceiling' : spent > alert ? 'alert' : 'ok'; // console: over the alert means above it, as the API's alarm judges
  const word = state === 'ceiling' ? 'Ceiling reached — new runs refused, running ones stopped' : state === 'alert' ? 'Over the alert threshold' : 'Within limits';
  return (
    <div className={cx('rq-spend', 'is-' + state, compact && 'is-compact')}>
      <div className="rq-spend-head">
        <span className="rq-spend-label">AI spend{compact ? '' : ', ' + period}</span>
        <span className="rq-spend-num"><strong>{money(spent, currency)}</strong> of {money(ceiling, currency)}</span>
      </div>
      <div className="rq-spend-track" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={ceiling} aria-valuenow={spent}
        aria-valuetext={money(spent, currency) + ' of ' + money(ceiling, currency) + ' ceiling; alert at ' + money(alert, currency) + '. ' + word}>
        <span className="rq-spend-fill" style={{ width: pct + '%' }} />
        <span className="rq-spend-tick is-alert" style={{ left: (alert / ceiling) * 100 + '%' }} title={'Alert at ' + money(alert, currency)} />
      </div>
      {compact ? null : (
        <div className="rq-spend-foot">
          <span className="rq-spend-state">{state !== 'ok' ? <Icon name={state === 'ceiling' ? 'octagon-pause' : 'triangle-alert'} size={14} /> : null}{word}</span>
          <span className="rq-spend-legend">Alert at {money(alert, currency)}</span>
        </div>
      )}
    </div>
  );
}

/* ───────────── Data ───────────── */

/** A tiny trend line. Always paired with a number or a word; the line alone is not the data. */
export function Sparkline({ values, width = 88, height = 24, label, tone = 'neutral' }: { values: number[]; width?: number; height?: number; label: string; tone?: Tone }) {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const pts = values.map((v, i) => [(i / (values.length - 1)) * (width - 4) + 2, height - 3 - ((v - min) / span) * (height - 6)]);
  const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg className={cx('rq-spark', 'rq-tone--' + tone)} width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label + ': ' + values.join(', ')}>
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.25" fill="currentColor" />
    </svg>
  );
}

/** One number that matters, with its change and trend. */
export function Metric({ label, value, unit, delta, trend, note, href }: { label: string; value: Node; unit?: string; delta?: { text: string; direction: 'up' | 'down' | 'flat'; sentiment?: 'good' | 'bad' | 'neutral' }; trend?: number[]; note?: Node; href?: string }) {
  const body = (
    <Fragment>
      <span className="rq-metric-label">{label}</span>
      <span className="rq-metric-row">
        <span className="rq-metric-value">{value}{unit ? <span className="rq-metric-unit">{unit}</span> : null}</span>
        {trend ? <Sparkline values={trend} label={label + ' trend'} /> : null}
      </span>
      {delta ? (
        <span className={cx('rq-metric-delta', 'is-' + (delta.sentiment || 'neutral'))}>
          <Icon name={delta.direction === 'up' ? 'arrow-up' : delta.direction === 'down' ? 'arrow-down' : 'minus'} size={14} />{delta.text}
        </span>
      ) : null}
      {note ? <span className="rq-metric-note">{note}</span> : null}
    </Fragment>
  );
  return href ? <A className="rq-metric is-link" href={href}>{body}</A> : <div className="rq-metric">{body}</div>;
}

export interface Column { key: string; label: string; align?: 'left' | 'right' | 'center'; sortable?: boolean; width?: string | number; render?: (row: any) => Node; hideBelow?: 'phone' | 'tablet' }
/** A dense, keyboard-friendly table with selection and sorting. */
export function Table({ columns, rows, rowKey = 'id', selectable, selected = [], onSelect, sort, onSort, activeKey, onRowClick, caption, dense = true, stickyHeader = true }: {
  columns: Column[]; rows: any[]; rowKey?: string; selectable?: boolean; selected?: string[]; onSelect?: (keys: string[]) => void; sort?: { key: string; dir: 'asc' | 'desc' }; onSort?: (key: string) => void; activeKey?: string; onRowClick?: (row: any) => void; caption?: string; dense?: boolean; stickyHeader?: boolean }) {
  const all = rows.map((r) => String(r[rowKey]));
  const allOn = all.length > 0 && all.every((k) => selected.includes(k));
  const someOn = !allOn && all.some((k) => selected.includes(k));
  const toggle = (k: string) => onSelect && onSelect(selected.includes(k) ? selected.filter((s) => s !== k) : [...selected, k]);
  return (
    <div className="rq-table-wrap">
      <table className={cx('rq-table', dense && 'is-dense', stickyHeader && 'is-sticky')}>
        {caption ? <caption className="rq-sr">{caption}</caption> : null}
        <thead>
          <tr>
            {selectable ? <th className="rq-td-check" scope="col"><Checkbox label="Select all rows" hideLabel checked={allOn} indeterminate={someOn} onChange={() => onSelect && onSelect(allOn ? [] : all)} /></th> : null}
            {columns.map((c) => {
              const s = sort && sort.key === c.key ? sort.dir : null;
              return (
                <th key={c.key} scope="col" style={{ width: c.width, textAlign: c.align }} className={c.hideBelow ? 'rq-hide-' + c.hideBelow : undefined}
                  aria-sort={s ? (s === 'asc' ? 'ascending' : 'descending') : c.sortable ? 'none' : undefined}>
                  {c.sortable ? (
                    <button type="button" className="rq-th-sort" onClick={() => onSort && onSort(c.key)}>
                      {c.label}<Icon name={s === 'asc' ? 'arrow-up' : s === 'desc' ? 'arrow-down' : 'arrow-up-down'} size={12} className={s ? '' : 'rq-th-idle'} />
                    </button>
                  ) : c.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const k = String(r[rowKey]); const on = selected.includes(k);
            return (
              <tr key={k} className={cx(on && 'is-selected', activeKey === k && 'is-active', onRowClick && 'is-clickable')} aria-selected={selectable ? on : undefined} aria-current={activeKey === k ? 'true' : undefined} /* console: announces the list cursor */
                onClick={onRowClick ? (e: any) => { if (!(e.target as HTMLElement).closest('input,button,a,label')) onRowClick(r); } : undefined}>
                {selectable ? <td className="rq-td-check"><Checkbox label={'Select ' + (r.name || k)} hideLabel checked={on} onChange={() => toggle(k)} /></td> : null}
                {columns.map((c) => <td key={c.key} style={{ textAlign: c.align }} className={c.hideBelow ? 'rq-hide-' + c.hideBelow : undefined}>{c.render ? c.render(r) : r[c.key] ?? <span className="rq-missing">—</span>}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The selection bar: how many are selected and the actions that are safe in bulk. */
export function BulkBar({ count, noun = 'candidate', children, onClear }: { count: number; noun?: string; children?: Node; onClear?: () => void }) {
  if (!count) return null;
  return (
    <div className="rq-bulk" role="region" aria-label="Bulk actions">
      <span className="rq-bulk-count"><strong>{count}</strong> {noun}{count === 1 ? '' : 's'} selected</span>
      <span className="rq-bulk-actions">{children}</span>
      <Button variant="quiet" size="sm" onClick={onClear}>Clear selection</Button>
    </div>
  );
}

/** An active filter, removable. Filters live in the URL so a view can be shared. */
export function FilterChip({ label, value, onRemove }: { label: string; value: Node; onRemove?: () => void }) {
  return (
    <span className="rq-filter">
      <span className="rq-filter-label">{label}:</span> <span className="rq-filter-value">{value}</span>
      {onRemove ? <button type="button" className="rq-filter-x" onClick={onRemove} aria-label={'Remove filter ' + label + ': ' + (typeof value === 'string' ? value : '')}><Icon name="x" size={14} /></button> : null}
    </span>
  );
}

/** Status tabs with counts. Arrow keys move between tabs. */
export function Tabs({ items, value, onChange, label }: { items: { key: string; label: string; count?: number | null }[]; value: string; onChange?: (k: string) => void; label: string }) {
  const refs = useRef({} as Record<string, HTMLButtonElement | null>);
  const onKey = (e: any, i: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0; if (!d) return;
    const n = items[(i + d + items.length) % items.length]; onChange && onChange(n.key); const el = refs.current[n.key]; el && el.focus(); e.preventDefault();
  };
  return (
    <div className="rq-tabs" role="tablist" aria-label={label}>
      {items.map((t, i) => (
        <button key={t.key} ref={(el: any) => (refs.current[t.key] = el)} type="button" role="tab" aria-selected={value === t.key} tabIndex={value === t.key ? 0 : -1}
          className={cx('rq-tab', value === t.key && 'is-on')} onClick={() => onChange && onChange(t.key)} onKeyDown={(e: any) => onKey(e, i)}>
          {t.label}{t.count != null ? <span className="rq-tab-count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/* ───────────── Review ───────────── */

const CYR: Record<string, string> = { а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', ђ: 'd', е: 'e', ж: 'z', з: 'z', и: 'i', ј: 'j', к: 'k', л: 'l', љ: 'lj', м: 'm', н: 'n', њ: 'nj', о: 'o', п: 'p', р: 'r', с: 's', т: 't', ћ: 'c', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'c', џ: 'dz', ш: 's', й: 'j', ы: 'y', э: 'e', ю: 'ju', я: 'ja', ь: '', ъ: '' };
function normChar(ch: string) {
  const l = ch.toLowerCase();
  if (CYR[l] != null) return CYR[l];
  if (l === 'đ') return 'd';
  if (l === ',') return '.';
  if (/\s/.test(l)) return ' ';
  return l.normalize('NFD').replace(/[̀-ͯ]/g, '') || l;
}
/** Search that ignores case, diacritics and script: "nis" finds "Niš", "beograd" finds "Београд", "21.1" finds "21,1". */
export function findMatches(text: string, query: string): [number, number][] {
  const q = Array.from(query.trim()).map(normChar).join('').replace(/ +/g, ' ');
  if (!q) return [];
  let norm = ''; const map: number[] = [];
  for (let i = 0; i < text.length; i++) { const n = normChar(text[i]); for (const c of n) { if (c === ' ' && norm.endsWith(' ')) continue; norm += c; map.push(i); } }
  const out: [number, number][] = []; let from = 0;
  while (true) { const at = norm.indexOf(q, from); if (at < 0) break; out.push([map[at], map[at + q.length - 1] + 1]); from = at + q.length; }
  return out;
}

/** The captured page beside the extraction: search it, see every hit, step through them. */
export function EvidencePane({ text, url, capturedAt, httpStatus = 200, query: qProp, onQueryChange, organizerUrl, height = 480, title = 'Captured source' }: { text: string; url: string; capturedAt: string; httpStatus?: number; query?: string; onQueryChange?: (q: string) => void; organizerUrl?: string; height?: number | string; title?: string }) {
  const [qLocal, setQLocal] = useState(qProp || '');
  const q = qProp != null ? qProp : qLocal;
  const setQ = (v: string) => { setQLocal(v); onQueryChange && onQueryChange(v); };
  const [cur, setCur] = useState(0);
  const matches = useMemo(() => findMatches(text || '', q), [text, q]);
  const body = useRef(null); const uid = useUid('rq-ev');
  useEffect(() => { setCur(0); }, [q]);
  useEffect(() => {
    const el = body.current as any; if (!el) return;
    const m = el.querySelector('mark.is-current'); if (!m) return;
    const top = m.offsetTop - el.clientHeight / 2 + m.offsetHeight / 2; el.scrollTop = Math.max(0, top);
  }, [cur, matches]);
  const go = (d: number) => matches.length && setCur((c: number) => (c + d + matches.length) % matches.length);
  const parts: Node[] = []; let last = 0;
  matches.forEach(([s, e], i) => { if (s > last) parts.push(text.slice(last, s)); parts.push(<mark key={i} className={i === cur ? 'is-current' : undefined}>{text.slice(s, e)}</mark>); last = e; });
  parts.push(text.slice(last));
  const ok = httpStatus >= 200 && httpStatus < 300;
  let host = url; try { host = new URL(url).host + new URL(url).pathname; } catch (e) { /* keep raw */ }
  return (
    <section className="rq-evidence" aria-labelledby={uid + '-t'}>
      <header className="rq-evidence-head">
        <div className="rq-evidence-title">
          <h3 id={uid + '-t'} className="text-heading">{title}</h3>
          <span className="rq-evidence-meta">
            <span className="rq-evidence-url" title={url}>{host}</span>
            <span aria-hidden="true">·</span><span>Captured {capturedAt}</span>
            <span aria-hidden="true">·</span><span className={cx('rq-evidence-http', !ok && 'is-bad')}>{ok ? null : <Icon name="triangle-alert" size={12} />}HTTP {httpStatus}</span>
          </span>
        </div>
        <div className="rq-evidence-links">
          <Button size="sm" variant="quiet" href={url} target="_blank" rel="noreferrer" iconRight="external-link">Open original</Button>
          {organizerUrl ? <Button size="sm" variant="quiet" href={organizerUrl} target="_blank" rel="noreferrer" iconRight="external-link">Organizer site</Button> : null}
        </div>
      </header>
      <div className="rq-evidence-search">
        <div className="rq-input rq-input--sm has-icon">
          <Icon name="text-search" />
          <input type="search" value={q} placeholder="Find in source, e.g. 21,1 or 09:00" aria-label="Find in captured source" aria-controls={uid + '-b'}
            onChange={(e: any) => setQ(e.target.value)}
            onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); go(e.shiftKey ? -1 : 1); } if (e.key === 'Escape') setQ(''); }} />
        </div>
        <span className="rq-evidence-count" aria-live="polite">{q ? (matches.length ? `${cur + 1} of ${matches.length}` : 'No matches') : ''}</span>
        <Button size="sm" variant="quiet" iconOnly icon="chevron-up" label="Previous match" shortcut="⇧↵" disabled={!matches.length} onClick={() => go(-1)} />
        <Button size="sm" variant="quiet" iconOnly icon="chevron-down" label="Next match" shortcut="↵" disabled={!matches.length} onClick={() => go(1)} />
      </div>
      <div className="rq-evidence-body text-mono" id={uid + '-b'} ref={body} tabIndex={0} style={{ height }} aria-label="Captured page text">{parts}</div>
    </section>
  );
}

/** One extracted value: shows whether the machine or a person set it, and checks it against the source. */
export function ExtractedField({ label, value, unit, origin = 'machine', editedBy, editedAt, raw, warning, onFind, onChange, mono, multiline }: {
  label: string; value: string | null; unit?: string; origin?: 'machine' | 'human'; editedBy?: string; editedAt?: string; raw?: string; warning?: string; onFind?: (q: string) => void; onChange?: (v: string) => void; mono?: boolean; multiline?: boolean }) {
  const [editing, setEditing] = useState(false); const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null); const uid = useUid('rq-xf');
  useEffect(() => { if (editing && inputRef.current) (inputRef.current as any).focus(); }, [editing]);
  const w = warning ? WARNINGS[warning] : null;
  const missing = value == null || value === '';
  const save = () => { setEditing(false); if (onChange && draft !== value) onChange(draft); };
  const Tag: any = multiline ? 'textarea' : 'input';
  return (
    <div className={cx('rq-xf', 'is-' + origin, missing && 'is-missing', w && w.blocking && 'is-blocking')}>
      <div className="rq-xf-label" id={uid + '-l'}>{label}</div>
      <div className="rq-xf-main">
        {editing ? (
          <div className="rq-xf-edit">
            <div className="rq-input rq-input--sm"><Tag ref={inputRef} aria-labelledby={uid + '-l'} value={draft} rows={multiline ? 4 : undefined}
              onChange={(e: any) => setDraft(e.target.value)} onKeyDown={(e: any) => { if (e.key === 'Enter' && !multiline) { e.preventDefault(); save(); } if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }} /></div>
            <Button size="sm" variant="primary" onClick={save}>Save</Button>
            <Button size="sm" variant="quiet" onClick={() => { setDraft(value || ''); setEditing(false); }}>Cancel</Button>
          </div>
        ) : (
          <div className="rq-xf-value">
            {missing ? <span className="rq-missing">{w && w.blocking ? <Fragment><Icon name="octagon-alert" size={14} />Missing — blocks publishing</Fragment> : 'Missing'}</span>
              : <span className={cx('rq-xf-text', mono && 'text-mono', 'is-' + origin)} title={origin === 'machine' ? 'Extracted by the pipeline, not yet checked by a person' : undefined}>{value}{unit ? ' ' + unit : ''}</span>}
            <span className="rq-xf-tools">
              {onFind && !missing ? <Button size="sm" variant="quiet" iconOnly icon="text-search" label={'Find ' + label.toLowerCase() + ' in source'} onClick={() => onFind(raw || value || '')} /> : null}
              {onChange ? <Button size="sm" variant="quiet" iconOnly={!missing} icon="pencil" label={missing ? 'Fill in' : 'Edit ' + label.toLowerCase()} onClick={() => setEditing(true)}>{missing ? 'Fill in' : undefined}</Button> : null}
            </span>
          </div>
        )}
        <div className="rq-xf-meta">
          {origin === 'machine' ? <span className="rq-xf-origin"><Icon name="bot" size={12} />Extracted</span>
            : <span className="rq-xf-origin"><Icon name="pencil" size={12} />Edited by {editedBy}{editedAt ? ' · ' + editedAt : ''}</span>}
          {raw && raw !== value ? <span className="rq-xf-raw">Source says <span className="text-mono-small">“{raw}”</span></span> : null}
          {w && !(w.blocking && missing) ? <Warning type={warning as string} compact /> : null}
        </div>
      </div>
    </div>
  );
}

/** What changed at the source: every field as Before and After, never colour alone. */
// console: a row may take a key, a node for its field, `lang` for its values and a `note` under the field; the column notes can be renamed.
export function Diff({ rows, showUnchanged, beforeNote = 'published', afterNote = 'source now' }: { rows: { key?: string; field: Node; before: string | null; after: string | null; lang?: string; note?: Node }[]; showUnchanged?: boolean; beforeNote?: string; afterNote?: string }) {
  const shown = rows.filter((r) => showUnchanged || r.before !== r.after);
  const v = (x: string | null, lang?: string) => (x == null || x === '' ? <span className="rq-missing">Missing</span> : lang ? <span lang={lang}>{x}</span> : x);
  return (
    <table className="rq-diff">
      <thead><tr><th scope="col">Field</th><th scope="col">Before <span className="rq-diff-note">({beforeNote})</span></th><th scope="col">After <span className="rq-diff-note">({afterNote})</span></th></tr></thead>
      <tbody>
        {shown.map((r) => {
          const same = r.before === r.after;
          return (
            <tr key={r.key ?? String(r.field)} className={same ? 'is-same' : undefined}>
              <th scope="row">{r.field}{r.note ? <span className="rq-diff-field-note">{r.note}</span> : null}</th>
              <td className={same ? '' : 'is-removed'}>{same ? v(r.before, r.lang) : <Fragment><span className="rq-diff-sign" aria-hidden="true">−</span><span className="rq-sr">Removed: </span><del>{v(r.before, r.lang)}</del></Fragment>}</td>
              <td className={same ? '' : 'is-added'}>{same ? <span className="rq-diff-same">Unchanged</span> : <Fragment><span className="rq-diff-sign" aria-hidden="true">+</span><span className="rq-sr">Added: </span><ins>{v(r.after, r.lang)}</ins></Fragment>}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The evidence for a suspected duplicate: each criterion in words, plus the overall strength. */
export function MatchEvidence({ strength, criteria, title, subtitle, action }: { strength: 'likely' | 'possible' | 'unlikely'; criteria: { label: string; result: 'same' | 'close' | 'different' | 'unknown'; detail: string }[]; title?: Node; subtitle?: Node; action?: Node }) {
  const icon: Record<string, string> = { same: 'check', close: 'equal-approximately', different: 'x', unknown: 'circle-help' };
  const word: Record<string, string> = { same: 'Same', close: 'Close', different: 'Different', unknown: 'Unknown' };
  return (
    <div className="rq-mev">
      {title ? <div className="rq-mev-head"><div><div className="rq-mev-title">{title}</div>{subtitle ? <div className="rq-mev-sub">{subtitle}</div> : null}</div><MatchStrength strength={strength} /></div> : null}
      <ul className="rq-mev-list">
        {criteria.map((c) => (
          <li key={c.label} className={'is-' + c.result}>
            <Icon name={icon[c.result]} size={14} /><span className="rq-mev-label">{c.label}</span><span className="rq-mev-word">{word[c.result]}</span><span className="rq-mev-detail">{c.detail}</span>
          </li>
        ))}
      </ul>
      {action ? <div className="rq-mev-action">{action}</div> : null}
    </div>
  );
}

/** Publish, link or reject — pinned so the evidence can never push it out of reach. */
// console: `actions` replaces Reject, Link and Publish, for the changed published event's own decisions; the reason has an id the disabled buttons are described by.
export function DecisionBar({ index, total, onPrev, onNext, onPublish, onLink, onReject, publishBlockedReason, busy, suggestion, actions }: { index?: number; total?: number; onPrev?: () => void; onNext?: () => void; onPublish?: () => void; onLink?: () => void; onReject?: () => void; publishBlockedReason?: string; busy?: 'publish' | 'link' | 'reject' | null; suggestion?: Node; actions?: Node }) {
  return (
    <div className="rq-decide" role="region" aria-label="Decision">
      <div className="rq-decide-nav">
        <Button size="sm" variant="quiet" iconOnly icon="chevron-left" label="Previous candidate" shortcut="K" onClick={onPrev} disabled={!onPrev} />
        {total ? <span className="rq-decide-pos">{index} of {total}</span> : null}
        <Button size="sm" variant="quiet" iconOnly icon="chevron-right" label="Next candidate" shortcut="J" onClick={onNext} disabled={!onNext} />
      </div>
      <div className="rq-decide-msg">
        {publishBlockedReason ? <span id={DECIDE_WHY_ID} className="rq-decide-blocked"><Icon name="octagon-alert" size={14} />{publishBlockedReason}</span> : suggestion ? <span className="rq-decide-suggest">{suggestion}</span> : null}
      </div>
      <div className="rq-decide-actions">
        {actions ?? <Fragment>
        <Button variant="danger-quiet" icon="circle-x" shortcut="R" onClick={onReject} loading={busy === 'reject'}>Reject…</Button>
        <Button variant="secondary" icon="link" shortcut="L" onClick={onLink} loading={busy === 'link'}>Link…</Button>
        <Button variant="primary" icon="circle-check" shortcut="P" onClick={onPublish} disabled={!!publishBlockedReason} loading={busy === 'publish'} aria-describedby={publishBlockedReason ? DECIDE_WHY_ID : undefined}>Publish</Button>
        </Fragment>}
      </div>
    </div>
  );
}

/* ───────────── Pipeline ───────────── */

/** One row of the exceptions inbox: what, why the machine stopped, and what it proposes. */
export function InboxItem({ reason, title, meta, proposal, age, actions, active, href }: { reason: string; title: Node; meta?: Node; proposal?: Node; age?: string; actions?: Node; active?: boolean; href?: string }) {
  const r = REASONS[reason] || { label: reason, icon: 'info', tone: 'neutral' as Tone };
  const T: any = href ? A : 'div';
  return (
    <div className={cx('rq-inbox', active && 'is-active')} aria-current={active ? 'true' : undefined} /* console: announces the list cursor */>
      <span className={cx('rq-inbox-reason', 'rq-tone--' + r.tone)}><Icon name={r.icon} size={14} />{r.label}</span>
      <T className="rq-inbox-title" href={href}>{title}</T>
      {meta ? <span className="rq-inbox-meta">{meta}</span> : null}
      {proposal ? <span className="rq-inbox-proposal"><Actor kind="machine" mode="suggested" compact />{proposal}</span> : null}
      <span className="rq-inbox-side">{age ? <span className="rq-inbox-age">{age}</span> : null}{actions ? <span className="rq-inbox-actions">{actions}</span> : null}</span>
    </div>
  );
}

/** A run's counts at a glance: fetched, unchanged, extracted, rejected, cost. */
export function RunSummary({ status, halted, stopped, trigger, source, started, duration, counts, cost, tokens }: { status: string; halted?: boolean; stopped?: boolean; trigger: 'manual' | 'scheduled' | 'import'; source: string; started: string; duration?: string; counts: { fetched: number; unchanged: number; extracted: number; rejected: number; failed?: number }; cost?: number; tokens?: number }) {
  const t = TRIGGERS[trigger];
  const cell = (label: string, n: number | undefined, tone?: string) => <div className={cx('rq-run-cell', tone)}><span className="rq-run-n">{n ?? '—'}</span><span className="rq-run-l">{label}</span></div>;
  return (
    <div className="rq-run">
      <div className="rq-run-head">
        <span className="rq-run-src">{source}</span>
        <Status domain="run" value={status} />
        {halted ? <Status domain="run" value="halted" /> : null}
        {stopped ? <Status domain="run" value="stopped" /> : null}{/* console: stopped by a pause */}
        <span className="rq-run-meta"><Icon name={t.icon} size={14} />{t.label} · {started}{duration ? ' · ' + duration : ''}</span>
      </div>
      <div className="rq-run-grid">
        {cell('pages fetched', counts.fetched)}{cell('unchanged, skipped', counts.unchanged)}{cell('events extracted', counts.extracted)}{cell('rejected by checks', counts.rejected)}
        {counts.failed != null ? cell('pages failed', counts.failed, counts.failed ? 'is-bad' : '') : null}
        {cost != null ? <div className="rq-run-cell"><span className="rq-run-n">${cost.toFixed(4)}</span><span className="rq-run-l">{tokens != null ? tokens.toLocaleString('en-GB') + ' tokens' : 'AI cost'}</span></div> : null}
      </div>
    </div>
  );
}

/** One line of the audit trail: who, did what, to what, when, why — and how to undo it. */
export function AuditEntry({ actor, mode = 'acted', action, target, time, reason, rule, undo, undone, evidenceHref }: { actor: { kind: 'machine' | 'person'; name?: string }; mode?: 'acted' | 'suggested'; action: string; target: Node; time: string; reason?: Node; rule?: string; undo?: { label: string; onClick?: () => void; refused?: string }; undone?: { by: string; at: string }; evidenceHref?: string }) {
  return (
    <div className={cx('rq-audit', actor.kind === 'machine' && 'is-machine', undone && 'is-undone')}>
      <span className="rq-audit-time">{time}</span>
      <Actor kind={actor.kind} name={actor.name} mode={mode} compact />
      <span className="rq-audit-what"><span className="rq-audit-action">{action}</span> <span className="rq-audit-target">{target}</span>
        {reason || rule ? <span className="rq-audit-why">{rule ? <span className="rq-audit-rule">Rule: {rule}</span> : null}{reason ? <span>{reason}</span> : null}{evidenceHref ? <A href={evidenceHref}>Evidence</A> : null}</span> : null}
      </span>
      <span className="rq-audit-undo">
        {undone ? <span className="rq-audit-undone"><Icon name="undo-2" size={14} />Undone by {undone.by} · {undone.at}</span>
          : undo ? (undo.refused ? <span className="rq-audit-refused" title={undo.refused}><Icon name="ban" size={14} />{undo.refused}</span>
            : <Button size="sm" variant="secondary" icon="undo-2" onClick={undo.onClick}>{undo.label}</Button>) : null}
      </span>
    </div>
  );
}

/* ───────────── Feedback ───────────── */

/** A message in the flow of the page. For refusals, quote the server's reason. */
export function Banner({ tone = 'info', title, children, action, onDismiss, reason }: { tone?: 'info' | 'positive' | 'caution' | 'critical'; title: Node; children?: Node; action?: Node; onDismiss?: () => void; reason?: string }) {
  const icon = { info: 'info', positive: 'circle-check', caution: 'triangle-alert', critical: 'octagon-alert' }[tone];
  return (
    <div className={cx('rq-banner', 'rq-tone--' + tone)} role={tone === 'critical' || tone === 'caution' ? 'alert' : 'status'}>
      <Icon name={icon} size={16} className="rq-banner-icon" />
      <div className="rq-banner-body">
        <p className="rq-banner-title">{title}</p>
        {children ? <div className="rq-banner-text">{children}</div> : null}
        {reason ? <p className="rq-banner-reason"><span className="rq-banner-reason-label">Reason given:</span> {reason}</p> : null}
      </div>
      {action ? <div className="rq-banner-action">{action}</div> : null}
      {onDismiss ? <Button size="sm" variant="quiet" iconOnly icon="x" label="Dismiss" onClick={onDismiss} /> : null}
    </div>
  );
}

/** Says honestly why there is nothing: nothing yet, nothing for this filter, all clear, or failed to load. */
export function EmptyState({ kind = 'first-use', title, children, action }: { kind?: 'first-use' | 'no-results' | 'all-clear' | 'error'; title: Node; children?: Node; action?: Node }) {
  const icon = { 'first-use': 'plus', 'no-results': 'filter', 'all-clear': 'inbox', error: 'octagon-alert' }[kind];
  return (
    <div className={cx('rq-empty', 'is-' + kind)} role={kind === 'error' ? 'alert' : undefined}>
      <span className="rq-empty-icon" aria-hidden="true"><Icon name={icon} size={20} /></span>
      <p className="rq-empty-title">{title}</p>
      {children ? <div className="rq-empty-text">{children}</div> : null}
      {action ? <div className="rq-empty-action">{action}</div> : null}
    </div>
  );
}

/** Loading placeholders sized exactly like what they stand in for, so nothing shifts. */
export function Skeleton({ width = '100%', height = 12, radius = 'var(--radius-xs)' }: { width?: number | string; height?: number | string; radius?: string }) {
  return <span className="rq-skel" aria-hidden="true" style={{ width, height, borderRadius: radius }} />;
}
export function SkeletonRows({ rows = 5, columns = ['40%', '20%', '15%', '15%'], label = 'Loading' }: { rows?: number; columns?: (string | number)[]; label?: string }) {
  return (
    <div className="rq-skel-rows" role="status" aria-label={label}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rq-skel-row">{columns.map((w, j) => <Skeleton key={j} width={w} height={j === 0 ? 14 : 12} />)}</div>
      ))}
    </div>
  );
}

/** Confirms an irreversible action by listing every record it touches, by name. */
// console: the app uses ds/ext/ConfirmDialog (Radix); ds/ext files import './ConfirmDialog', never ConfirmDialog from '../components'.
export function ConfirmDialog({ open = true, title, children, keep, items, itemsLabel, confirmLabel, cancelLabel = 'Cancel', tone = 'critical', onConfirm, onCancel, busy, refused }: { open?: boolean; title: Node; children?: Node; keep?: { name: Node; meta?: Node }; items: { name: Node; meta?: Node }[]; itemsLabel?: Node; confirmLabel: string; cancelLabel?: string; tone?: 'critical' | 'primary'; onConfirm?: () => void; onCancel?: () => void; busy?: boolean; refused?: string }) {
  const uid = useUid('rq-dlg'); const cancelRef = useRef(null);
  useEffect(() => { if (open && cancelRef.current) (cancelRef.current as any).focus(); }, [open]);
  if (!open) return null;
  return (
    <div className="rq-dialog-backdrop" onKeyDown={(e: any) => { if (e.key === 'Escape' && onCancel) onCancel(); }}>
      <div className="rq-dialog" role="alertdialog" aria-modal="true" aria-labelledby={uid + '-t'} aria-describedby={uid + '-d'}>
        <h2 className="rq-dialog-title text-heading" id={uid + '-t'}>{title}</h2>
        <div className="rq-dialog-body" id={uid + '-d'}>
          {children}
          {keep ? <div className="rq-dialog-keep"><span className="rq-dialog-group"><Icon name="circle-check" size={14} />Keep</span><div className="rq-dialog-item"><span className="rq-dialog-name">{keep.name}</span>{keep.meta ? <span className="rq-dialog-meta">{keep.meta}</span> : null}</div></div> : null}
          <div className="rq-dialog-list">
            {/* console: a primary confirmation (bulk publish) lists its records as kept, not rejected. */}
            <span className={cx('rq-dialog-group', tone === 'critical' && 'is-critical')}><Icon name={tone === 'critical' ? 'circle-x' : 'circle-check'} size={14} />{itemsLabel || 'Reject'} ({items.length})</span>
            <ul>{items.map((it, i) => <li key={i} className="rq-dialog-item"><span className="rq-dialog-name">{it.name}</span>{it.meta ? <span className="rq-dialog-meta">{it.meta}</span> : null}</li>)}</ul>
          </div>
          {refused ? <Banner tone="critical" title="The server refused this" reason={refused} /> : null}
        </div>
        <div className="rq-dialog-foot">
          <button ref={cancelRef} type="button" className="rq-btn rq-btn--quiet rq-btn--md" onClick={onCancel}><span className="rq-btn-label">{cancelLabel}</span></button>
          <Button variant={tone === 'critical' ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}

/** A short confirmation after an action, with Undo. */
export function Toast({ children, actionLabel = 'Undo', onAction, onDismiss }: { children: Node; actionLabel?: string | null; onAction?: () => void; onDismiss?: () => void }) {
  return (
    <div className="rq-toast" role="status" aria-live="polite">
      <span className="rq-toast-text">{children}</span>
      {actionLabel ? <button type="button" className="rq-toast-action" onClick={onAction}>{actionLabel}</button> : null}
      {onDismiss ? <button type="button" className="rq-toast-x" onClick={onDismiss} aria-label="Dismiss"><Icon name="x" size={14} /></button> : null}
    </div>
  );
}

/* ───────────── Navigation ───────────── */

export interface NavItem { key: string; label: string; icon: string; count?: number | null; countTone?: 'neutral' | 'caution' | 'critical'; href?: string }
/** The left navigation, with the workspace switcher that moves a person between operator and organizer work. */
export function SideNav({ workspace, sections, active, onNavigate, onSwitch, footer, renderSwitch }: { workspace: { name: string; kind: 'operator' | 'organizer'; status?: string }; sections: { label?: string; items: NavItem[] }[]; active?: string; onNavigate?: (k: string) => void; onSwitch?: () => void; footer?: Node; renderSwitch?: (button: Node) => Node }) {
  return (
    <nav className="rq-nav" aria-label="Main">
      <div className="rq-nav-brand"><Logo size={20} product={workspace.kind === 'operator' ? 'Console' : null} /></div>
      {/* console: renderSwitch lets the host attach its menu to the switcher button. */}
      {(renderSwitch || ((b: Node) => b))(
      <button type="button" className="rq-nav-switch" onClick={onSwitch} aria-haspopup="menu">
        <span className="rq-nav-switch-icon" aria-hidden="true"><Icon name={workspace.kind === 'operator' ? 'shield-check' : 'building-2'} size={14} /></span>
        {/* console: the status sits on the kind line, so a long name keeps the width. */}
        <span className="rq-nav-switch-text"><span className="rq-nav-switch-kind">{workspace.kind === 'operator' ? 'Runqo operations' : 'Organization'}{workspace.status && workspace.status !== 'approved' ? <Status domain="organization" value={workspace.status} size="sm" compact /> : null}</span><span className="rq-nav-switch-name" title={workspace.name}>{workspace.name}</span></span>
        <Icon name="chevrons-up-down" size={14} />
        <span className="rq-sr">Switch workspace</span>
      </button>
      )}
      {sections.map((s, i) => (
        <div key={i} className="rq-nav-section">
          {s.label ? <div className="rq-nav-label">{s.label}</div> : null}
          <ul>
            {s.items.map((it) => (
              <li key={it.key}>
                <A className={cx('rq-nav-item', active === it.key && 'is-on')} href={it.href || '#' + it.key} aria-current={active === it.key ? 'page' : undefined}
                  onClick={onNavigate ? (e: any) => { e.preventDefault(); onNavigate(it.key); } : undefined}>
                  <Icon name={it.icon} size={16} /><span className="rq-nav-text">{it.label}</span>
                  {it.count ? <span className={cx('rq-nav-count', it.countTone && 'is-' + it.countTone)}>{it.count}</span> : null}
                </A>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {footer ? <div className="rq-nav-foot">{footer}</div> : null}
    </nav>
  );
}

/** The operator navigation, as the information architecture defines it. */
// console: Quality sits after Runs in Pipeline.
export const OPERATOR_NAV: { label?: string; items: NavItem[] }[] = [
  { items: [{ key: 'inbox', label: 'Inbox', icon: 'inbox' }] },
  { label: 'Catalog', items: [{ key: 'candidates', label: 'Candidates', icon: 'list-checks' }, { key: 'duplicates', label: 'Duplicate groups', icon: 'copy' }, { key: 'events', label: 'Published events', icon: 'calendar-days' }] },
  { label: 'Pipeline', items: [{ key: 'sources', label: 'Sources', icon: 'globe' }, { key: 'runs', label: 'Runs', icon: 'activity' }, { key: 'quality', label: 'Quality', icon: 'gauge' }, { key: 'rules', label: 'Trust rules', icon: 'shield-check' }, { key: 'audit', label: 'Audit trail', icon: 'history' }, { key: 'transfer', label: 'Import & export', icon: 'upload' }] },
  { label: 'Platform', items: [{ key: 'organizations', label: 'Organizations', icon: 'building-2' }, { key: 'sponsored', label: 'Sponsored content', icon: 'megaphone' }, { key: 'surveys', label: 'Surveys', icon: 'message-square-text' }] },
];
/** The organizer navigation, per organization. */
export const ORGANIZER_NAV: { label?: string; items: NavItem[] }[] = [
  { items: [{ key: 'overview', label: 'Overview', icon: 'activity' }, { key: 'events', label: 'Events', icon: 'calendar-days' }, { key: 'runners', label: 'Interested runners', icon: 'users' }] },
  { label: 'Organization', items: [{ key: 'members', label: 'Members', icon: 'user' }, { key: 'settings', label: 'Settings', icon: 'settings' }] },
];

/* ───────────── Help ───────────── */
// console: the guide, pipeline stage and duplicate-group anchors render through `A`, so internal hrefs route client-side.

/** The Help panel's frame: header with close, a body that scrolls on its own, a footer. Docked beside the page; `sheet` inside the host's dialog. */
export function HelpPanel({ title, onClose, back, children, footer, sheet, label = 'Help', headingRef }: { title: Node; onClose?: () => void; back?: { label: string; onClick: () => void }; children?: Node; footer?: Node; sheet?: boolean; label?: string; headingRef?: any }) {
  const heading = <h2 ref={headingRef} tabIndex={-1} className="rq-help-title text-heading">{title}</h2>;
  const close = onClose ? <Button size="sm" variant="quiet" iconOnly icon="x" label="Close help" shortcut="Esc" onClick={onClose} /> : null;
  const T: any = sheet ? 'div' : 'aside';
  return (
    <T className={cx('rq-help', sheet && 'is-sheet')} aria-label={sheet ? undefined : label}>
      <header className="rq-help-head">
        {back ? (
          <Fragment>
            <div className="rq-help-bar"><Button size="sm" variant="quiet" icon="arrow-left" className="rq-help-back" onClick={back.onClick}>{back.label}</Button>{close}</div>
            {heading}
          </Fragment>
        ) : <div className="rq-help-bar">{heading}{close}</div>}
      </header>
      <div className="rq-help-body">{children}</div>
      {footer ? <footer className="rq-help-foot">{footer}</footer> : null}
    </T>
  );
}

/** Words and what they mean. Each term is drawn with its own component (a Status, Warning or Health), never as plain text. */
export function TermList({ items }: { items: { term: Node; meaning: Node }[] }) {
  return (
    <dl className="rq-terms">
      {items.map((t, i) => <div key={i} className="rq-term"><dt>{t.term}</dt><dd>{t.meaning}</dd></div>)}
    </dl>
  );
}

const KEY_WORDS: Record<string, true> = { '/': true, then: true, or: true, '+': true };
/** "About this page": what the page is for, what you can do, what its words mean and its keys, always in that order. */
export function HelpTopic({ title, summary, actions, terms, shortcuts, children }: { title: Node; summary: Node; actions?: { verb: string; does: Node }[]; terms?: { term: Node; meaning: Node }[]; shortcuts?: { keys: string[]; does: Node }[]; children?: Node }) {
  const uid = useUid('rq-topic');
  return (
    <section className="rq-topic" aria-labelledby={uid + '-t'}>
      <p className="rq-topic-kicker">About this page</p>
      <h3 id={uid + '-t'} className="rq-topic-title">{title}</h3>
      <div className="rq-topic-summary">{summary}</div>
      {actions && actions.length ? (
        <div className="rq-topic-part">
          <h4 className="rq-topic-h">What you can do here</h4>
          <dl className="rq-topic-actions">{actions.map((a, i) => <div key={i}><dt>{a.verb}</dt><dd>{a.does}</dd></div>)}</dl>
        </div>
      ) : null}
      {terms && terms.length ? <div className="rq-topic-part"><h4 className="rq-topic-h">What the words mean</h4><TermList items={terms} /></div> : null}
      {shortcuts && shortcuts.length ? (
        <div className="rq-topic-part">
          <h4 className="rq-topic-h">Keyboard</h4>
          <dl className="rq-topic-keys">
            {shortcuts.map((s, i) => (
              <div key={i}><dt>{s.keys.map((k, j) => KEY_WORDS[k] && j > 0 && j < s.keys.length - 1 ? <span key={j} className="rq-topic-keysep">{k}</span> : <Kbd key={j}>{k}</Kbd>)}</dt><dd>{s.does}</dd></div>
            ))}
          </dl>
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Short guides, a row each. Opening one replaces the Help panel's body. */
export function GuideList({ items, label }: { items: { key?: string; icon: string; title: string; summary?: Node; href?: string; onClick?: () => void }[]; label?: string }) {
  const uid = useUid('rq-guides');
  return (
    <div className="rq-guides">
      {label ? <h3 id={uid + '-t'} className="rq-guides-label">{label}</h3> : null}
      <ul aria-labelledby={label ? uid + '-t' : undefined}>
        {items.map((g) => {
          const body = (
            <Fragment>
              <span className="rq-guide-icon" aria-hidden="true"><Icon name={g.icon} size={16} /></span>
              <span className="rq-guide-text"><span className="rq-guide-title">{g.title}</span>{g.summary ? <span className="rq-guide-sum">{g.summary}</span> : null}</span>
              <Icon name="chevron-right" size={16} className="rq-guide-chev" />
            </Fragment>
          );
          return <li key={g.key || g.title}>{g.href ? <A className="rq-guide" href={g.href} onClick={g.onClick}>{body}</A> : <button type="button" className="rq-guide" onClick={g.onClick}>{body}</button>}</li>;
        })}
      </ul>
    </div>
  );
}

/** A setup checklist whose steps tick themselves from real data. `done` and `total` count the required steps. */
export function Checklist({ title, done, total, onHide, children }: { title: Node; done: number; total: number; onHide?: () => void; children: Node }) {
  const uid = useUid('rq-cl');
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <section className="rq-cl" aria-labelledby={uid + '-t'}>
      <header className="rq-cl-head">
        <div className="rq-cl-heading">
          <h2 id={uid + '-t'} className="rq-cl-title text-heading">{title}</h2>
          <div className="rq-cl-progress">
            <span className="rq-cl-track" aria-hidden="true"><span className="rq-cl-fill" style={{ width: pct + '%' }} /></span>
            <span className="rq-cl-count">{done} of {total} done</span>
          </div>
        </div>
        {onHide ? <Button size="sm" variant="quiet" onClick={onHide}>Hide checklist</Button> : null}
      </header>
      <ol className="rq-cl-list">{children}</ol>
    </section>
  );
}

const STEP: Record<string, { icon: string; word: string }> = { done: { icon: 'circle-check', word: 'Done' }, todo: { icon: 'circle', word: 'To do' }, locked: { icon: 'lock', word: 'Locked' } };
/** One setup step: done, to do (with its one action) or locked (with its reason, always visible). */
export function ChecklistItem({ state, title, children, action, lockedReason, optional }: { state: 'done' | 'todo' | 'locked'; title: Node; children?: Node; action?: Node; lockedReason?: Node; optional?: boolean }) {
  const s = STEP[state];
  return (
    <li className={cx('rq-cl-item', 'is-' + state)}>
      <Icon name={s.icon} size={16} label={s.word} className="rq-cl-icon" />
      <div className="rq-cl-body">
        <p className="rq-cl-step">{title}{optional ? <span className="rq-cl-opt"> · Optional</span> : null}</p>
        {children ? <div className="rq-cl-text">{children}</div> : null}
        {state === 'locked' && lockedReason ? <p className="rq-cl-locked">{lockedReason}</p> : null}
      </div>
      {state === 'todo' && action ? <div className="rq-cl-action">{action}</div> : null}
    </li>
  );
}

type PipelineStage = 'sources' | 'runs' | 'extraction' | 'candidates' | 'duplicates' | 'rules' | 'inbox' | 'events' | 'audit';
const PIPELINE: Record<string, { name: string; who?: 'machine' | 'person'; text: string }> = {
  sources: { name: 'Sources', who: 'person', text: 'Race websites an operator adds, each on its own schedule.' },
  runs: { name: 'Runs', who: 'machine', text: 'Fetch each source’s pages on schedule or on demand, skipping pages that haven’t changed.' },
  extraction: { name: 'Extraction', who: 'machine', text: 'The AI reads each page and pulls out events and their races.' },
  candidates: { name: 'Candidates', who: 'machine', text: 'Each event is checked for warnings, and suspected duplicates go into ' },
  rules: { name: 'Trust rules set to Act', who: 'machine', text: 'Decide on their own and record why.' },
  inbox: { name: 'Inbox', who: 'person', text: 'What the machine couldn’t decide, one reason each.' },
  events: { name: 'Published events', text: 'Live in the app for runners.' },
  audit: { name: 'Audit trail', text: 'Every decision, by the pipeline or a person, with Undo.' },
};
function PipeLine({ from, arrow = true }: { from: 'machine' | 'person'; arrow?: boolean }) {
  return <span className={cx('rq-pmap-line', 'is-' + from, arrow && 'has-arrow')} aria-hidden="true" />;
}
function PipePart({ id, links, compact }: { id: PipelineStage; links: Partial<Record<PipelineStage, string>>; compact?: boolean }) {
  const s = PIPELINE[id]; const href = links[id];
  const dup = links.duplicates;
  return (
    <div className="rq-pmap-part">
      <div className="rq-pmap-head">
        {href ? <A className="rq-pmap-name" href={href}>{s.name}</A> : <span className="rq-pmap-name">{s.name}</span>}
        {s.who ? <span className="rq-pmap-who"><Icon name={s.who === 'machine' ? 'bot' : 'user'} size={12} />{s.who === 'machine' ? 'Pipeline' : 'Operator'}</span>
          : id === 'events' ? <Status domain="event" value="live" size="sm" /> : null}
      </div>
      {compact ? null : <p className="rq-pmap-text">{s.text}{id === 'candidates' ? <Fragment>{dup ? <A href={dup}>duplicate groups</A> : 'duplicate groups'}.</Fragment> : null}</p>}
    </div>
  );
}
/** How the catalog gets built, drawn in the system's own language: the machine's stages dashed with the bot, people's stages solid. */
export function PipelineMap({ links = {}, compact, label = 'How the catalog gets built' }: { links?: Partial<Record<PipelineStage, string>>; compact?: boolean; label?: string }) {
  const stage = (id: PipelineStage) => <div className={cx('rq-pmap-stage', 'is-' + (PIPELINE[id].who || 'outcome'))}><PipePart id={id} links={links} compact={compact} /></div>;
  return (
    <ol className={cx('rq-pmap', compact && 'is-compact')} aria-label={label}>
      <li>{stage('sources')}</li>
      <li><PipeLine from="person" />{stage('runs')}</li>
      <li><PipeLine from="machine" />{stage('extraction')}</li>
      <li><PipeLine from="machine" />{stage('candidates')}</li>
      <li className="rq-pmap-fork">
        <PipeLine from="machine" arrow={false} />
        <div className="rq-pmap-split">
          <span className="rq-pmap-bar is-top is-machine" aria-hidden="true" />
          <ul className="rq-pmap-branches">
            <li><PipeLine from="machine" />{stage('rules')}<PipeLine from="machine" arrow={false} /></li>
            <li><PipeLine from="machine" />{stage('inbox')}<PipeLine from="person" arrow={false} /></li>
          </ul>
          <span className="rq-pmap-bar is-left is-machine" aria-hidden="true" />
          <span className="rq-pmap-bar is-right is-person" aria-hidden="true" />
        </div>
        <PipeLine from="person" />
      </li>
      <li>
        <div className="rq-pmap-stage is-outcome"><PipePart id="events" links={links} compact={compact} /><PipePart id="audit" links={links} compact={compact} /></div>
      </li>
    </ol>
  );
}
